import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { Box, Checkbox, List, Spinner, Tag, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import ItemAssignees from 'modules/processes/components/ItemAssignees'
import { AssigneeStrategy } from 'modules/processes/components/ItemAssignees/types'
import { DueDate } from 'modules/processes/components/MaterializedProcessesTable/NextTaskDueDate'
import type { Item, MaterializedItem } from 'modules/processes/domain'
import { useResolveMaterializedTask } from 'modules/processes/hooks/tasks/useResolveMaterializedTask'
import { useProcessContext } from 'modules/processes/pages/Process/ProcessRootPage'
import { TaskIdentifier } from 'modules/tasks/lib/task'

import { MaterializedItemTaskType } from './useTaskItems'

interface Props {
  materializedProcessId: number
  isCurrentStep: boolean
  materializedItem: MaterializedItem
  task?: MaterializedItemTaskType
}

const MaterializedTask = ({
  materializedItem,
  isCurrentStep,
  materializedProcessId,
  task,
}: Props) => {
  const location = useLocation()
  const { category } = useParams<{ category: string }>()
  const { navigation } = useNavigation()

  const navigate = useNavigate()

  const { process } = useProcessContext()

  const { resolveTask, loading: running } = useResolveMaterializedTask()

  const locationState: Record<string, string> = location.state

  const backTo = locationState?.backTo
    ? locationState.backTo
    : navigation.processes.template.id.fullPath({ category: category!, id: process.id! })

  const { performAction: markTask } = useAsyncAction(async () => {
    if (!task) return null

    await resolveTask({
      variables: {
        id: task.id,
        done: materializedItem.status == 'pending' ? true : false,
        identifier: TaskIdentifier.Workflow,
      },
      refecthVars: {
        materializedProcessId,
        taskId: task.id,
        identifier: TaskIdentifier.Workflow,
      },
    })
  })

  const disabled = !isCurrentStep || !materializedItem || running
  const finished = Boolean(task?.completedAt)
  const accessIds = task?.assignees.map((assignee) => assignee.id)

  const detailPath = navigation.processes.template.id.processInstance.item.fullPath({
    category: category!,
    materializedItemId: materializedItem.id!,
    id: process.id,
    materializedProcessId: materializedProcessId,
  })

  const isMandatory = materializedItem.item?.mandatory

  return (
    <List.ClickableItem // Move to clickable
      navigateTo={() => {
        navigate(detailPath, {
          state: {
            backTo: backTo,
          },
        })
      }}
      key={materializedItem.id}
    >
      <Box flexDirection="row" gap="s16">
        <List.ActionItem>
          <Checkbox size="large" disabled={disabled} checked={finished} onChange={markTask} />
        </List.ActionItem>

        <Text.Normal
          weight="medium"
          color={isCurrentStep ? undefined : 'grey600'}
          stroked={finished}
          ellipsis
        >
          {task?.name}
        </Text.Normal>
        {running && <Spinner size="s24" />}
      </Box>

      <Box gap="s16" flexDirection="row" alignItems="center" flexNoShrink>
        {!finished && task?.dueOn && <DueOnComponent task={task} />}
        {isMandatory && (
          <Tag type={finished ? Tag.Types.grey3 : Tag.Types.champagne}>
            <Text.Normal size="200" weight="medium" color={finished ? 'grey600' : 'grey1000'}>
              {i18n.t('processes.materialize_process.detail.mandatory')}
            </Text.Normal>
          </Tag>
        )}

        <Box position="relative">
          {finished && (
            <Box
              height="full"
              background="grey100"
              borderRadius={{ all: 'abs008' }}
              style={{
                width: 'calc(100% + 12px)',
                position: 'absolute',
                left: '-12px',
                zIndex: '5',
                opacity: '50%',
                pointerEvents: 'none',
              }}
            />
          )}

          <ItemAssignees
            count={task?.assigneesCount}
            accessIds={accessIds}
            itemAssigneeIds={materializedItem?.item?.assigneeIds}
            assigneeStrategy={AssigneeStrategy.MANUAL_SELECTION}
          />
        </Box>
      </Box>
    </List.ClickableItem>
  )
}

interface ConfigItemsProps {
  item: Item
}

const ConfigItem = ({ item }: ConfigItemsProps) => {
  const finished = false
  const accessIds =
    item.itemAssignee?.assigneeStrategy === AssigneeStrategy.MANUAL_SELECTION
      ? item?.itemAssignee?.assigneeIds
      : []

  return (
    <List.Item key={item.id}>
      <Box flexDirection="row" gap="s16">
        <Box alignSelf="center">
          <List.ActionItem>
            <Checkbox size="large" disabled checked={finished} onChange={undefined} />
          </List.ActionItem>
        </Box>
        <Box gap="s6" flexDirection="column">
          <Text.Normal weight="medium" color="grey600" stroked={finished}>
            {item.title}
          </Text.Normal>
        </Box>
      </Box>

      <Box gap="s16" flexDirection="row" alignItems="center">
        {item.mandatory && (
          <Tag type={Tag.Types.champagne} disabled>
            {i18n.t('processes.materialize_process.detail.mandatory')}
          </Tag>
        )}
        <ItemAssignees
          accessIds={accessIds}
          itemAssigneeIds={item.itemAssignee?.assigneeIds}
          assigneeStrategy={item.itemAssignee?.assigneeStrategy as AssigneeStrategy}
        />
      </Box>
    </List.Item>
  )
}

export const ConfigItemComponent = observer(ConfigItem)

interface DueOnComponentProps {
  task?: MaterializedItemTaskType
}

const DueOnComponent = ({ task }: DueOnComponentProps) => {
  if (!task?.dueOn) return null

  return <DueDate date={stringToDate(task.dueOn, 'backend_datetime')} />
}

export default observer(MaterializedTask)
